import { useState, useEffect, useRef, type ReactNode } from 'react'

import styles from './ProfilePage.module.scss'
import CalculatorIcon from '../../assets/svgIcons/CalculatorIcon'
import ChevronDownIcon from '../../assets/svgIcons/ChevronDownIcon'
import CloseIcon from '../../assets/svgIcons/CloseIcon'
import DepartmentIcon from '../../assets/svgIcons/DepartmentIcon'
import HashIcon from '../../assets/svgIcons/HashIcon'
import HomeIcon from '../../assets/svgIcons/HomeIcon'
import LocationIcon from '../../assets/svgIcons/LocationIcon'
import MailIcon from '../../assets/svgIcons/MailIcon'
import WarnIcon from '../../assets/svgIcons/WarnIcon'
import { UserProfile } from '../../types/user'
// ─── Types ────────────────────────────────────────────────────────────────────
interface InfoRowProps {
  icon: ReactNode
  label: string
  value?: string
  error?: string
}

interface SettingRowProps {
  icon: ReactNode
  label: string
  value?: string
}

interface ProfileProps {
  userData?: UserProfile
}

interface ProfileForm {
  firstName: string
  lastName: string
  country: string
}

// ─── Countries list ───────────────────────────────────────────────────────────
const COUNTRIES = [
  'Afghanistan',
  'Australia',
  'Brazil',
  'Canada',
  'China',
  'Denmark',
  'Egypt',
  'France',
  'Germany',
  'India',
  'Indonesia',
  'Italy',
  'Japan',
  'Mexico',
  'Netherlands',
  'New Zealand',
  'Nigeria',
  'Norway',
  'Pakistan',
  'Philippines',
  'Russia',
  'Saudi Arabia',
  'South Africa',
  'South Korea',
  'Spain',
  'Sweden',
  'Thailand',
  'Turkey',
  'United Kingdom',
  'United States',
]

// ─── Sub-components ───────────────────────────────────────────────────────────
const ProfileInfoRow = ({ icon, label, value, error }: InfoRowProps): JSX.Element => (
  <div className={styles.infoRow}>
    <span className={styles.infoIcon}>{icon}</span>
    <div className={styles.infoContent}>
      <span className={styles.infoLabel}>{label}</span>
      {error ? (
        <span className={styles.infoError}>
          <WarnIcon />
          {error}
        </span>
      ) : (
        <span className={styles.infoValue}>{value ?? '–'}</span>
      )}
    </div>
  </div>
)

const SettingRow = ({ icon, label, value }: SettingRowProps): JSX.Element => (
  <div className={styles.settingRow}>
    <span className={styles.settingIcon}>{icon}</span>
    <div className={styles.settingContent}>
      <span className={styles.settingLabel}>{label}</span>
      <span className={styles.settingValue}>{value ?? '–'}</span>
    </div>
  </div>
)

// ─── Edit Profile Sidebar ─────────────────────────────────────────────────────
interface EditProfileSidebarProps {
  initialForm: ProfileForm
  onSave: (form: ProfileForm) => void
  onClose: () => void
}

const EditProfileSidebar = ({ initialForm, onSave, onClose }: EditProfileSidebarProps): JSX.Element => {
  const [form, setForm] = useState<ProfileForm>(initialForm)
  const sidebarRef = useRef<HTMLDivElement>(null)

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  // Trap scroll on body
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  const handleChange = (field: keyof ProfileForm, value: string) => setForm(prev => ({ ...prev, [field]: value }))

  const handleSave = () => {
    onSave(form)
    onClose()
  }

  const initial = (form.firstName.charAt(0) || 'P').toUpperCase()

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/45 z-899 transition-opacity duration-150 pointer-events-none"
        aria-hidden="true"
      />

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className="fixed top-0 right-0 h-full sm:w-[440px] md:w-[600px] lg:w-[760px] xl:w-[920px] hd:w-[1080px] bg-white shadow-2xl z-[1000] flex flex-col animate-slide-in"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sidebar-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 id="sidebar-title" className="text-lg font-semibold text-gray-900">
            Edit profile
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close sidebar"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Avatar */}
          <div className="flex justify-start mb-6">
            <div className={`flex items-center justify-center  ${styles.avatar} ${styles['avatar--modal']}`}>
              {initial}
            </div>
          </div>

          {/* First / Last name row */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1.5">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={form.firstName}
                onChange={e => handleChange('firstName', e.target.value)}
                placeholder="First Name"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1.5">
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={form.lastName}
                onChange={e => handleChange('lastName', e.target.value)}
                placeholder="Last Name"
              />
            </div>
          </div>

          {/* Email — read-only */}
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              value="puja.saraiwala@vml.com"
              readOnly
            />
          </div>

          {/* Agency — read-only */}
          <div className="mb-4">
            <label htmlFor="agency" className="block text-sm font-medium text-gray-700 mb-1.5">
              Agency
            </label>
            <input
              id="agency"
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              value="tccc"
              readOnly
            />
            <p className="mt-2 text-sm text-gray-600">
              Wrong Agency?{' '}
              <button className="text-blue-600 hover:text-blue-700 font-medium hover:underline">
                Request change of agency
              </button>
            </p>
          </div>

          {/* Country — select */}
          <div className="mb-4">
            <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1.5">
              Country
            </label>
            <div className="relative">
              <select
                id="country"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                value={form.country}
                onChange={e => handleChange('country', e.target.value)}
              >
                <option value="">Select country</option>
                {COUNTRIES.map(c => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                <ChevronDownIcon />
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────
const ProfilePage = ({ userData }: ProfileProps): JSX.Element => {
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false)
  useEffect(() => {
    if (userData) {
      setProfile({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        country: userData.country || '',
      })
    }
  }, [userData])

  const [profile, setProfile] = useState<ProfileForm>({
    firstName: '',
    lastName: '',
    country: '',
  })

  const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(' ')
  const handleSave = (updated: ProfileForm) => setProfile(updated)

  return (
    <div className={styles.page}>
      <main className="page-shell flex flex-col gap-6">
        <h1 className={`${styles.pageTitle} headline-m `}>Profile</h1>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* ── Left: user info card ── */}
          <div className={`pt-6 pr-8 pb-8 pl-8 rounded-lg ${styles.card}`}>
            <div className={`flex justify-between mb-[1.5rem] pb-[1rem] ${styles.cardHeader}`}>
              <div className="flex justify-between items-start gap-9">
                {userData?.avatarUrl ? (
                  <img
                    src={userData.avatarUrl}
                    alt=""
                    className={`flex items-center justify-center ${styles.avatar} ${styles['avatar--lg']}`}
                  />
                ) : (
                  <div
                    className={`flex items-center justify-center ${styles.avatar} ${styles['avatar--lg']}`}
                    aria-label={fullName}
                  >
                    {profile.firstName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <p className={`title-xxs mb-[0.25rem] ${styles.userName}`}>{fullName}</p>
                  <p className={`body-m ${styles.userOrg}`}>{userData?.agency || 'tccc'}</p>
                </div>
              </div>
              <button
                className={`body-s flex items-center hidden m-0 ${styles.editBtn}`}
                onClick={() => setIsEditOpen(true)}
              >
                Edit
              </button>
            </div>

            <ProfileInfoRow icon={<MailIcon />} label="Email" value={userData?.email || '-'} />
            <ProfileInfoRow
              icon={<LocationIcon />}
              label="Country"
              value={profile.country || undefined}
              error={!profile.country ? 'Please specify your Country' : undefined}
            />
            <ProfileInfoRow icon={<DepartmentIcon />} label="Department" value={userData?.department} />
          </div>

          {/* ── Right: settings card ── */}
          <div className="flex flex-col gap-[1.5rem]">
            <div className={`pt-6 pr-8 pb-8 pl-8 rounded-lg ${styles.card}`}>
              <div className={`flex justify-between mb-[1.5rem] pb-[1rem] ${styles.cardHeader}`}>
                <h2 className={`title-xxs ${styles.cardTitle}`}>Settings</h2>
                <button className={`body-s flex items-center m-0 hidden ${styles.editBtn}`}>Edit</button>
              </div>

              <SettingRow icon={<HomeIcon />} label="Home workspace" />
              <SettingRow icon={<HomeIcon />} label="Home Playground hub" />
              <SettingRow icon={<CalculatorIcon />} label="Date format" value="DD/MM/YYYY" />
              <SettingRow icon={<HashIcon />} label="Number format" value="0,000,000,000.000" />
            </div>
          </div>
        </div>
      </main>

      {/* ── Edit Profile Sidebar ── */}
      {isEditOpen && (
        <EditProfileSidebar initialForm={profile} onSave={handleSave} onClose={() => setIsEditOpen(false)} />
      )}
    </div>
  )
}

export default ProfilePage
